import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle, Clock, Globe } from 'lucide-react';
import { sendContactEmail } from '../services/emailService';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus('');
    
    try {
      const result = await sendContactEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });
      
      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus(''), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent('Hello! I would like to inquire about ESN Publications services.');
    window.open(`https://wa.me/918838173189?text=${message}`, '_blank');
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section className="hero-section text-center" style={{ 
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9), rgba(118, 75, 162, 0.9)), url("https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="container-custom">
          <h1 className="heading-1 text-white mb-6">Contact Us</h1>
          <p className="body-large text-white mb-8" style={{ maxWidth: '700px', margin: '0 auto 2rem', opacity: 0.95 }}>
            Get in touch with us for publications, conferences, awards, and collaboration opportunities
          </p>
        </div>
      </section>

      <div className="container-custom" style={{ padding: '80px 24px' }}>
        {/* Contact Info Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '30px', 
          marginBottom: '80px' 
        }}>
          {/* Phone Card */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea20 0%, #764ba210 100%)',
            padding: '3px',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '17px',
              padding: '40px',
              textAlign: 'center',
              height: '100%'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 25px',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
              }}>
                <Phone size={32} color="white" />
              </div>
              <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>
                Phone Numbers
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <a href="tel:+918838173189" style={{
                  color: '#667eea',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#5a67d8'}
                onMouseLeave={(e) => e.target.style.color = '#667eea'}>
                  +91 88381 73189
                </a>
                <a href="tel:+918667767076" style={{
                  color: '#667eea',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#5a67d8'}
                onMouseLeave={(e) => e.target.style.color = '#667eea'}>
                  +91 86677 67076
                </a>
              </div>
            </div>
          </div>

          {/* Email Card */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea20 0%, #764ba210 100%)',
            padding: '3px',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '17px',
              padding: '40px',
              textAlign: 'center',
              height: '100%'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 25px',
                boxShadow: '0 8px 25px rgba(72, 187, 120, 0.3)'
              }}>
                <Mail size={32} color="white" />
              </div>
              <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>
                Email Address
              </h3>
              <a href="mailto:contactus@esnresearch.in" style={{
                color: '#48bb78',
                fontSize: '1.1rem',
                fontWeight: 600,
                textDecoration: 'none',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#38a169'}
              onMouseLeave={(e) => e.target.style.color = '#48bb78'}>
                contactus@esnresearch.in
              </a>
            </div>
          </div>

          {/* Address Card */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea20 0%, #764ba210 100%)',
            padding: '3px',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '17px',
              padding: '40px',
              textAlign: 'center',
              height: '100%'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 25px',
                boxShadow: '0 8px 25px rgba(237, 137, 54, 0.3)'
              }}>
                <MapPin size={32} color="white" />
              </div>
              <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>
                Office Address
              </h3>
              <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', fontWeight: 500 }}>
                3/151 A, Muthuramalingapuram<br/>
                Kalloorani P.O, Tk, Aruppukkottai,<br/>
                Tamil Nadu-626105, India
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form and Map */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'start' }}>
          {/* Contact Form */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea20 0%, #764ba210 100%)',
            padding: '3px',
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '17px',
              padding: '50px'
            }}>
              <h2 style={{ color: '#2d3748', fontSize: '2rem', fontWeight: 700, marginBottom: '30px' }}>
                Send us a Message
              </h2>

              {submitStatus === 'success' && (
                <div style={{
                  backgroundColor: '#c6f6d5',
                  color: '#22543d',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '25px',
                  textAlign: 'center',
                  fontWeight: 600
                }}>
                  Message sent successfully! We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div style={{
                  backgroundColor: '#fed7d7',
                  color: '#c53030',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '25px',
                  textAlign: 'center',
                  fontWeight: 600
                }}>
                  Failed to send message. Please try again.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gap: '25px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: errors.name ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = errors.name ? '#e53e3e' : '#e2e8f0'}
                      />
                      {errors.name && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.name}</span>}
                    </div>

                    <div>
                      <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: errors.phone ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = errors.phone ? '#e53e3e' : '#e2e8f0'}
                      />
                      {errors.phone && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.phone}</span>}
                    </div>
                  </div>

                  <div>
                    <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: errors.email ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = errors.email ? '#e53e3e' : '#e2e8f0'}
                    />
                    {errors.email && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.email}</span>}
                  </div>

                  <div>
                    <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: errors.subject ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = errors.subject ? '#e53e3e' : '#e2e8f0'}
                    />
                    {errors.subject && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.subject}</span>}
                  </div>

                  <div>
                    <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: errors.message ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        resize: 'vertical',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = errors.message ? '#e53e3e' : '#e2e8f0'}
                    />
                    {errors.message && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.message}</span>}
                  </div>

                  <div style={{ display: 'flex', gap: '15px' }}>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        flex: 1,
                        padding: '16px',
                        backgroundColor: isSubmitting ? '#a0aec0' : '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Send size={20} />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>

                    <button
                      type="button"
                      onClick={openWhatsApp}
                      style={{
                        padding: '16px 20px',
                        backgroundColor: '#25d366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#128c7e'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#25d366'}
                    >
                      <MessageCircle size={20} />
                      WhatsApp
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Map and Office Hours */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            {/* Map */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea20 0%, #764ba210 100%)',
              padding: '3px',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '17px',
                padding: '30px'
              }}>
                <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px', textAlign: 'center' }}>
                  Find Us Here
                </h3>
                <div style={{ borderRadius: '15px', overflow: 'hidden', boxShadow: '0 5px 20px rgba(0,0,0,0.1)' }}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3929.8234567890123!2d78.1650608!3d9.4638088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b013bb041fc3af3%3A0xa2c85233d5076c57!2sAruppukkottai%2C%20Tamil%20Nadu%20626105!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="ESN Publications Location"
                  />
                </div>
              </div>
            </div>

            {/* Office Hours */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea20 0%, #764ba210 100%)',
              padding: '3px',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '17px',
                padding: '30px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 8px 25px rgba(159, 122, 234, 0.3)'
                }}>
                  <Clock size={28} color="white" />
                </div>
                <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>
                  Office Hours
                </h3>
                <div style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.8' }}>
                  <p><strong>Monday - Friday:</strong> 9:00 AM - 6:00 PM</p>
                  <p><strong>Saturday:</strong> 9:00 AM - 2:00 PM</p>
                  <p><strong>Sunday:</strong> Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;